'use client';

import { createContext, useContext } from 'react';
import type { TileAnimState, TileHolderSnapshot } from '../board-canvas/TileEffectsOverlay';
import type { TileAnimKind } from '../panels/tile-anim-demo.types';

/**
 * Stage phase for a slot in the visual lifecycle.
 *
 * The lobby `LobbyBoardStage` mounts up to two slots simultaneously. The
 * `data-stage-phase` attribute on each slot's root element drives the
 * CSS opacity transition in `globals.css`:
 *
 *  - `incoming-loading` — slot is being preloaded behind the current
 *    one. Opacity is 0 with no transition; the readiness gate hasn't
 *    elapsed yet, so the user must not see a partially-painted board.
 *  - `incoming-fading-in` — readiness has fired and the slot is now
 *    animating from 0 → 1 opacity over `BOARD_CROSSFADE_MS`. The
 *    outgoing slot animates in the opposite direction in lockstep.
 *  - `current` — slot is fully visible and stable. Opacity is 1, no
 *    transition. This is the only phase in which a slot accepts
 *    interactions (drawer-driven animations etc. all route here).
 *  - `outgoing-fading-out` — slot is being faded away while the
 *    incoming one rises. Opacity 1 → 0 over `BOARD_CROSSFADE_MS`.
 *    Interactions are locked so a click during the fade can't trigger
 *    a transaction against a board the user is leaving.
 */
export type BoardStagePhase =
  | 'incoming-loading'
  | 'incoming-fading-in'
  | 'current'
  | 'outgoing-fading-out';

/**
 * Animation API a slot installs into the stage when it owns the
 * URL-active board. Drawers, the tutorial bridge, and the dev tile-
 * anim demo panel all dispatch through the stage; the stage routes
 * the call to whichever slot has registered last (which by
 * construction is the one whose `boardId === targetBoardId`).
 */
export interface BoardStageAnimationApi {
  /** Plays the cinematic buyout (shatter + new-owner reveal). */
  playBuyoutAnimation: (
    seatIndex: number,
    previousHolder: TileHolderSnapshot,
    newHolder: TileHolderSnapshot,
  ) => void;
  /** Plays the cinematic claim (vacant → owner reveal). */
  playClaimAnimation: (
    seatIndex: number,
    newHolder: TileHolderSnapshot,
  ) => void;
  /** Dev tile-anim demo trigger (random N held seats). */
  triggerTileAnim: (kind: TileAnimKind, count?: number) => void;
  /** Direct setter the tutorial bridge talks to. */
  setSingleTileAnim: (seatIndex: number, animState: TileAnimState) => void;
}

/** Public surface exposed to `LobbyClient` via `useBoardStageContext()`. */
export interface BoardStageContextValue {
  /** The board the user currently sees on screen (slot in the
   *  `current` phase). Differs from `targetBoardId` while a
   *  crossfade is running; back in sync once the incoming slot
   *  settles. Drives the chip-rail reset trigger and any other
   *  panel that should react to the *visible* board change rather
   *  than the URL change. */
  displayedBoardId: string;
  /** True while a crossfade is mid-flight (incoming-fading-in /
   *  outgoing-fading-out). Replaces the previous `isSwapping`
   *  flag the canvases used to pull from `useBoardSwapTransition`.
   *  Consumers gate behaviour that should not run mid-transition
   *  (e.g. drawer auto-open on hover) on this. */
  interactionsLocked: boolean;
  /** Plays a buyout cinematic on whichever slot owns the URL-active
   *  board. No-op if no slot has registered yet (cold mount before
   *  the first canvas paint). */
  playBuyoutAnimation: BoardStageAnimationApi['playBuyoutAnimation'];
  /** Sibling of `playBuyoutAnimation` for the claim path. */
  playClaimAnimation: BoardStageAnimationApi['playClaimAnimation'];
  /** Dev-only — fires the tile-anim demo on the active slot. */
  triggerTileAnim: BoardStageAnimationApi['triggerTileAnim'];
  /** Internal: slots install / remove their animation API as they
   *  flip between active-target and not. Call sites are constrained
   *  to `BoardStageSlot`; LobbyClient never calls this directly. */
  __registerActiveSlot: (api: BoardStageAnimationApi) => () => void;
}

/**
 * `null` here matches the "no provider mounted" cold path. Surfaces
 * outside the lobby tree (404, tutorial pre-mount, etc.) that import
 * `useBoardStageContext` will throw at first use rather than silently
 * no-op the animation API — that's an explicit signal that whoever's
 * calling the context didn't render under `<LobbyBoardStage>`.
 */
const BoardStageContext = createContext<BoardStageContextValue | null>(null);

export const BoardStageProvider = BoardStageContext.Provider;

/**
 * Read the stage context from inside a render tree mounted under
 * `<LobbyBoardStage>`. Throws when the provider is missing because
 * the only consumers are `LobbyClient` and `BoardStageSlot`, both of
 * which are guaranteed by construction to be descendants. Catching
 * this at call time (rather than returning `null` and letting every
 * caller defensively check) surfaces wiring mistakes loudly during
 * dev instead of producing silent no-op animations on staging.
 */
export function useBoardStageContext(): BoardStageContextValue {
  const ctx = useContext(BoardStageContext);
  if (!ctx) {
    throw new Error(
      'useBoardStageContext must be used inside <LobbyBoardStage>',
    );
  }
  return ctx;
}

/** Optional read for surfaces that may render outside the stage
 *  (e.g. dev panels conditionally mounted on routes the lobby
 *  doesn't own). Returns `null` when no provider is in scope. */
export function useBoardStageContextOptional(): BoardStageContextValue | null {
  return useContext(BoardStageContext);
}
